
import { useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import LoginForm from "@/components/investor/LoginForm";
import PortfolioSummary from "@/components/investor/PortfolioSummary";
import InvestmentList from "@/components/investor/InvestmentList";
import ContactSection from "@/components/investor/ContactSection";
import LoadingState from "@/components/investor/LoadingState";
import ErrorState from "@/components/investor/ErrorState";
import PortalHeader from "@/components/investor/PortalHeader";
import EmptyState from "@/components/investor/EmptyState";
import { useInvestmentData } from "@/hooks/useInvestmentData";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

const InvestorPortal = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { investments, isLoading: dataLoading, error, debugInfo, refetch } = useInvestmentData();

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (isLoading) {
    return <LoadingState debugInfo={debugInfo} />;
  }

  if (!user) {
    return <LoginForm />;
  }

  if (dataLoading) {
    return <LoadingState debugInfo={debugInfo} />;
  }

  if (error) {
    return <ErrorState error={error} debugInfo={debugInfo} onRetry={refetch} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <PortalHeader 
            onLogout={handleSignOut}
            debugInfo={debugInfo}
          />

          {investments.length === 0 ? (
            <EmptyState onRefresh={refetch} />
          ) : (
            <>
              <PortfolioSummary investments={investments} />
              <InvestmentList investments={investments} />
            </>
          )}
          
          <ContactSection />
        </div>
      </div>
    </div>
  );
};

export default InvestorPortal;
